"use client";

import * as React from "react";
import { Play, Pause } from "lucide-react";
import { scaledPx } from "./utils";
import { cn } from "@/lib/utils";

const BUTTON_SIZE = 56;
const ICON_SIZE = 24;
const CORNER_OFFSET = 24;

interface VideoPlayPauseButtonProps {
    videoRef: React.RefObject<HTMLVideoElement | null>;
}

/**
 * Hover-revealed corner button for play/pause. Reads/writes the underlying
 * `<video>` element's paused state directly via ref. State is DOM-managed and
 * resets when the video unmounts (template/scene change).
 *
 * The parent of the video container must set `group/scene-video` so the
 * `group-hover` reveal works.
 */
export function VideoPlayPauseButton({ videoRef }: VideoPlayPauseButtonProps) {
    const [paused, setPaused] = React.useState(false);

    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const sync = () => setPaused(video.paused);
        video.addEventListener("play", sync);
        video.addEventListener("pause", sync);
        sync();
        return () => {
            video.removeEventListener("play", sync);
            video.removeEventListener("pause", sync);
        };
    }, [videoRef]);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            void video.play();
        } else {
            video.pause();
        }
    };

    const Icon = paused ? Play : Pause;

    return (
        <button
            type="button"
            aria-label={paused ? "Play video" : "Pause video"}
            onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onClick={handleClick}
            className={cn(
                "absolute z-30",
                "inline-flex items-center justify-center rounded-full",
                "bg-black/60 hover:bg-black/80 backdrop-blur-sm",
                "text-white !cursor-pointer pointer-events-auto",
                "opacity-0 group-hover/scene-video:opacity-100 focus-visible:opacity-100",
                "transition-opacity duration-150"
            )}
            style={{
                bottom: scaledPx(CORNER_OFFSET),
                insetInlineEnd: scaledPx(CORNER_OFFSET),
                width: scaledPx(BUTTON_SIZE),
                height: scaledPx(BUTTON_SIZE),
            }}
        >
            <Icon
                style={{
                    width: scaledPx(ICON_SIZE),
                    height: scaledPx(ICON_SIZE),
                }}
                fill="currentColor"
            />
        </button>
    );
}
